import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, useQuery, useMutation } from '../lib/convexApi';
import PhoneVerification from './PhoneVerification';
import PhoneInput from './PhoneInput';

const JoinTournament: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const tournament = useQuery(api.tournaments.getTournamentByInvite, 
    inviteCode ? { inviteCode } : "skip"
  );
  const registerForTournament = useMutation(api.players.registerForTournament);
  
  const [currentPlayer, setCurrentPlayer] = useState<any>(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const getDefaultCountryCode = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('preferredCountryCode') || '+52';
    }
    return '+52';
  };

  const [formData, setFormData] = useState({
    partnerName: '',
    partnerPhone: '',
    partnerCountryCode: getDefaultCountryCode(),
  });
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleVerificationComplete = (player: any) => {
    setCurrentPlayer(player);
    setShowRegistration(true);
  };

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPlayer || !tournament) return;

    setIsRegistering(true);
    setError('');

    try {
      const fullPartnerPhone = formData.partnerPhone ?
        formData.partnerCountryCode + formData.partnerPhone.replace(/\D/g, '') :
        undefined;

      const result = await registerForTournament({
        tournamentId: tournament._id,
        playerId: currentPlayer.playerId,
        partnerName: formData.partnerName || undefined,
        partnerPhone: fullPartnerPhone,
      });

      setSuccess(true);
      
      // Generate WhatsApp share message
      const shareUrl = `${window.location.origin}/join/${inviteCode}`;
      const shareText = encodeURIComponent(
        `🎾 Join our padel tournament!\n\n${tournament.name}\n📅 ${new Date(tournament.date).toLocaleDateString()}\n📍 ${tournament.club}\n\nClick here to register: ${shareUrl}`
      );
      
      // Show success message with WhatsApp share option
      setTimeout(() => {
        if (result.status === 'waitlist') {
          alert('You have been added to the waitlist. We\'ll notify you if a spot opens up!');
        }
      }, 100);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register');
    } finally {
      setIsRegistering(false);
    }
  };

  const shareOnWhatsApp = () => {
    if (!tournament) return;
    
    const shareUrl = `${window.location.origin}/join/${inviteCode}`;
    const shareText = encodeURIComponent(
      `🎾 Join our padel tournament!\n\n${tournament.name}\n📅 ${new Date(tournament.date).toLocaleDateString()}\n📍 ${tournament.club}\n\nClick here to register: ${shareUrl}`
    );
    
    window.open(`https://wa.me/?text=${shareText}`, '_blank');
  };

  if (!inviteCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Código de Invitación Inválido</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-800">← Volver al Inicio</Link>
        </div>
      </div>
    );
  }

  if (tournament === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Cargando torneo...</p>
        </div>
      </div>
    );
  }

  if (tournament === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Torneo No Encontrado</h1>
          <p className="text-gray-600 mb-6">El código de invitación "{inviteCode}" no es válido.</p>
          <Link to="/" className="text-blue-600 hover:text-blue-800">← Volver al Inicio</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">¡Inscripción Exitosa!</h1>
            <p className="text-gray-600 mb-6">
              Te has registrado exitosamente en {tournament.name}
            </p>
            
            <button
              onClick={shareOnWhatsApp}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 mb-4"
            >
              📱 Compartir en WhatsApp
            </button>
            
            <Link 
              to="/" 
              className="block text-blue-600 hover:text-blue-800"
            >
              ← Volver al Inicio
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-6">
          <img
            src="/torneopadel.png"
            alt="TorneoPadel"
            className="w-32 h-32 mx-auto mb-4 object-contain"
          />
        </div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Unirse al Torneo</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-800">← Volver al Inicio</Link>
        </div>

        {/* Tournament Info */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{tournament.name}</h2>
          <div className="space-y-2 text-gray-600">
            <p>📅 {new Date(tournament.date).toLocaleDateString('es-MX')} a las {tournament.time || '18:00'}</p>
            <p>📍 {tournament.club}</p>
            <p>🎾 Formato: {tournament.format === 'americano' ? 'Americano' : 'Rey de la Cancha'}</p>
            <p>📋 Inscripción: {tournament.registrationType === 'couples' ? 'Por Parejas' : 'Individual'}</p>
            <p>👥 {tournament.currentPlayers}/{tournament.maxPlayers} jugadores registrados</p>
            {tournament.description && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-1">📝 Instrucciones:</p>
                <p className="text-sm">{tournament.description}</p>
              </div>
            )}
            {tournament.status === 'locked' && (
              <p className="text-red-600 font-semibold">🔒 Inscripciones cerradas</p>
            )}
          </div>
        </div>

        {tournament.status === 'locked' ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Inscripciones Cerradas</h3>
            <p className="text-yellow-700">Este torneo ya no acepta nuevas inscripciones.</p>
          </div>
        ) : !showRegistration ? (
          <PhoneVerification onVerificationComplete={handleVerificationComplete} />
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Completar Inscripción</h3>
            
            <form onSubmit={handleRegistration} className="space-y-4">
              {tournament.registrationType === 'couples' && (
                <>
                  <div>
                    <label htmlFor="partnerName" className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de tu Pareja
                    </label>
                    <input
                      type="text"
                      id="partnerName"
                      value={formData.partnerName}
                      onChange={(e) => setFormData(prev => ({ ...prev, partnerName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nombre de tu pareja"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono de tu Pareja (Opcional)
                    </label>
                    <PhoneInput
                      countryCode={formData.partnerCountryCode}
                      phoneNumber={formData.partnerPhone}
                      onCountryCodeChange={(code) => setFormData(prev => ({ ...prev, partnerCountryCode: code }))}
                      onPhoneNumberChange={(number) => setFormData(prev => ({ ...prev, partnerPhone: number }))}
                      required={false}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Si tu pareja está registrada, recibirá una notificación
                    </p>
                  </div>
                </>
              )}

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

              <button
                type="submit"
                disabled={isRegistering}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
              >
                {isRegistering ? 'Registrando...' : 'Inscribirse al Torneo'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinTournament;